import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Clock, Search, Plus } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setTimeFilter, setSearchTerm } from '../../store/dashboardSlice';
import CategorySection from '../CategorySection/CategorySection';
import AddWidgetModal from '../AddWidgetModal/AddWidgetModal';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { categories, timeFilter, searchTerm } = useAppSelector(state => state.dashboard);
  const [isAddWidgetModalOpen, setIsAddWidgetModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [showAddWidgetDropdown, setShowAddWidgetDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowAddWidgetDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(e.target.value));
  };

  const handleTimeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setTimeFilter(e.target.value as 'last1day' | 'last2days' | 'last24hours'));
  };

  const handleRefresh = () => {
    // Just refresh the data, don't reload the page
    dispatch(setTimeFilter(timeFilter));
  };

  const handleGlobalAddWidget = (categoryId: string) => {
    console.log('Global add widget clicked for category:', categoryId);
    setSelectedCategoryId(categoryId);
    setIsAddWidgetModalOpen(true);
    setShowAddWidgetDropdown(false);
  };

  const toggleDropdown = () => {
    console.log('Dropdown toggle clicked, current state:', showAddWidgetDropdown);
    
    if (!showAddWidgetDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
    
    setShowAddWidgetDropdown(!showAddWidgetDropdown);
  };

  const openAddWidgetModal = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setIsAddWidgetModalOpen(true);
  };

  const closeAddWidgetModal = () => {
    setIsAddWidgetModalOpen(false);
    setSelectedCategoryId('');
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1 className="dashboard-title">CNAPP Dashboard</h1>
        </div>
        
        <div className="header-right">
          <div className="search-container">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search widgets..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>

          <div className="add-widget-dropdown" ref={dropdownRef}>
            <button 
              ref={buttonRef}
              onClick={toggleDropdown}
              className={`global-add-button ${showAddWidgetDropdown ? 'active' : ''}`}
            >
              <Plus size={18} />
              Add Widget
            </button>
            {showAddWidgetDropdown && (
              <div 
                className="add-widget-menu"
                style={{
                  position: 'fixed',
                  top: `${dropdownPosition.top}px`,
                  right: `${dropdownPosition.right}px`,
                  zIndex: 99999
                }}
              >
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleGlobalAddWidget('cspm');
                  }} 
                  className="menu-item"
                >
                  <span className="menu-icon">🥧</span>
                  <span>CSPM (Pie Chart)</span>
                </button>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleGlobalAddWidget('cwpp');
                  }} 
                  className="menu-item"
                >
                  <span className="menu-icon">📈</span>
                  <span>CWPP (Line Graph)</span>
                </button>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleGlobalAddWidget('registry');
                  }} 
                  className="menu-item"
                >
                  <span className="menu-icon">📊</span>
                  <span>Registry (Progress Bars)</span>
                </button>
              </div>
            )}
          </div>
          
          <div className="time-filter-container">
            <Clock className="time-icon" size={18} />
            <select 
              value={timeFilter} 
              onChange={handleTimeFilterChange}
              className="time-filter-select"
            >
              <option value="last24hours">Last 24 Hours</option>
              <option value="last1day">Last 1 Day</option>
              <option value="last2days">Last 2 Days</option>
            </select>
          </div>
          
          <button onClick={handleRefresh} className="refresh-button">
            <RefreshCw size={18} />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {categories.map((category) => (
          <CategorySection
            key={category.id}
            category={category}
            searchTerm={searchTerm}
            onAddWidget={() => openAddWidgetModal(category.id)}
          />
        ))}
      </main>

      {/* Add Widget Modal */}
      {isAddWidgetModalOpen && (
        <AddWidgetModal
          categoryId={selectedCategoryId}
          onClose={closeAddWidgetModal}
        />
      )}
    </div>
  );
};

export default Dashboard;
